import { useTextDomainContext } from "@/app/providers";
import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useDeleteUserCompany, useUserCompanies } from "@/hooks";
import { ConfirmNotification, TableView1 } from "..";
import { Button, IconButton, Tooltip } from "@mui/material";
import { bool } from "prop-types";
import { MdDelete } from "react-icons/md";
// import { Spinner } from "react-bootstrap";
import { AddUserCompany } from "./AddUserCompany";

type UserCustomersProps = {
  showAdd?: boolean;
  showDelete?: boolean;
};

type CustomerRow = {
  id?: string | number;
  name?: string;
  role?: string;
  [key: string]: unknown;
};

type DeleteMultipleIds = Array<string | number>;

type DeleteDialogParameters = {
  action?: string;
  ids?: DeleteMultipleIds;
  customerName?: string;
  userId?: string;
};

type ResultPayload = {
  result?: string;
  parameters?: DeleteDialogParameters;
};

type RowActionParams = {
  row: CustomerRow;
};

export const UserCustomers = (props: UserCustomersProps) => {
  const { userId } = useParams();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();
  // const { user } = users();
  // const location = useLocation();
  const { customers, userName, loading } = useUserCompanies(userId);

  const { deleteUserCompany, deleting } = useDeleteUserCompany();

  //region React Hook (useState)
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [dialogParameters, setDialogParameters] =
    useState<DeleteDialogParameters>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [editRow, setEditRow] = useState<CustomerRow>({});
  //endregion React Hook (useState)

  // Use props directly instead of state to avoid cascading renders
  const showAdd = props.showAdd;
  const showDelete = props.showDelete;

  //region Handlers
  const handleOnClickDeleteMultiple = (ids: DeleteMultipleIds) => {
    setDialogParameters({
      action: "delete-multi",
      ids: ids,
    });
    setShowConfirm(true);
  };

  const handleOnClickDeleteSingle = (row: CustomerRow) => {
    setDialogParameters({
      action: "delete-single",
      customerName: row.name,
      userId: userId,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: ResultPayload) => {
    if (obj.result === "yes") {
      deleteUserCompany({
        userId: obj?.parameters?.userId,
        customerName: obj?.parameters?.customerName,
      });
    }
    setShowConfirm(false);
  };

  const handleCloseAddForm = () => {
    setAddFormOpen(false);
    setEditRow({});
  };

  // const handleEdit = (row) => {
  //   setEditRow(row);
  //   setAddFormOpen(true);
  // };

  const performAction = (row: CustomerRow, action: string) => {
    switch (action) {
      case "delete":
        handleOnClickDeleteSingle(row);
        break;
      default:
        break;
    }
  };
  //endregion Handlers

  //region Table Settings
  const postToolbar = () => {
    return (
      <>
        <Button
          variant="contained"
          onClick={() => {
            navigate(-1);
          }}
          sx={{
            mx: 1,
            color: "secondary",
          }}
        >
          {gettext("Back")}
        </Button>
        {showAdd && (
          <Button
            variant="contained"
            onClick={() => {
              setAddFormOpen(true);
            }}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {gettext("Add Customer")}
          </Button>
        )}
      </>
    );
  };

  const columnFormatter = (params: RowActionParams) => {
    return (
      <div className="w-100 d-flex justify-content-center">
        {showDelete && (
          <Tooltip title={gettext("Delete")} arrow>
            <IconButton onClick={() => performAction(params.row, "delete")}>
              <MdDelete size={16} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
    },
    {
      field: "name",
      headerName: gettext("Name"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "role",
      headerName: gettext("Role"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "actions",
      headerName: gettext("Actions"),
      hideable: false,
      minWidth: 120,
      maxWidth: 200,
      // flex: 1,
      // minWidth: 80,
      // maxWidth: 80,
      // headerAlign: "center",
      // type: "actions",
      renderCell: (params: RowActionParams) => columnFormatter(params),
    },
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        label: true,
      },
    },
  };
  //endregion Table Settings

  return (
    <>
      <TableView1
        title={gettext("Companies for ") + userName}
        rows={customers}
        // rows={[]}
        columns={columns}
        loading={loading}
        checkboxSelection={false}
        initialState={initialState}
        // preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        // headerRight={headerRight()}
        showGridToolbar={true}
        showGridToolbarDeleteButton={true}
        showGridToolbarDensitySelector={false}
        showGridToolbarColumnsButton={false}
        showGridToolbarQuickFilter={false}
        showGridToolbarFilterButton={false}
        showGridToolbarExport={false}
        // onRowSelectionModelChange={handleOnRowSelectionModelChange}
        onClickDeleteMultiple={handleOnClickDeleteMultiple}
      />

      <AddUserCompany
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
      />
      {deleting ? (
        <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">
          {/*<Spinner animation="border" variant="light" />*/}
        </div>
      ) : null}
    </>
  );
};

UserCustomers.propTypes = {
  showAdd: bool,
  showDelete: bool,
};
